import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {GeometryAttributeNode, meshGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createMeshGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {MeshGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type MeshGeometryGrid2Parameters = {
    originX: ScalarArg | null
    originY: ScalarArg | null
    basis1X: ScalarArg | null
    basis1Y: ScalarArg | null
    basis2X: ScalarArg | null
    basis2Y: ScalarArg | null
    numXSegments: ScalarArg | null
    numYSegments: ScalarArg | null
}

const definition: TemplateNodeDefinition<MeshGeometryGrid2Parameters> = {
    nodeClass: "MeshGeometryGrid2",
    validation: {
        parameters: z.object({
            originX: z.nullable(scalarArg),
            originY: z.nullable(scalarArg),
            basis1X: z.nullable(scalarArg),
            basis1Y: z.nullable(scalarArg),
            basis2X: z.nullable(scalarArg),
            basis2Y: z.nullable(scalarArg),
            numXSegments: z.nullable(scalarArg),
            numYSegments: z.nullable(scalarArg),
        }),
        returns: getTemplateNodeReturns(),
    },
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class MeshGeometryGrid2 extends GeometryAttributeNode<2, MeshGeometryGrid2Parameters> implements MeshGeometryAttributeLikeNode {
    get [meshGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: MeshGeometryGrid2Parameters,
    ): parameters is MeshGeometryGrid2Parameters & NonNullableParameters<MeshGeometryGrid2Parameters> {
        return (
            parameters.originX !== null &&
            parameters.originY !== null &&
            parameters.basis1X !== null &&
            parameters.basis1Y !== null &&
            parameters.basis2X !== null &&
            parameters.basis2Y !== null &&
            parameters.numXSegments !== null &&
            parameters.numYSegments !== null
        )
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<MeshGeometryGrid2Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<2> | null>; invalidator?: BuilderInlet<null>} {
        const {originX, originY, basis1X, basis1Y, basis2X, basis2Y, numXSegments, numYSegments} = parameters

        const [originXValid, originXInvalid] = scope.branch(evaluator.evaluate(originX))
        const [originYValid, originYInvalid] = scope.branch(evaluator.evaluate(originY))
        const [basis1XValid, basis1XInvalid] = scope.branch(evaluator.evaluate(basis1X))
        const [basis1YValid, basis1YInvalid] = scope.branch(evaluator.evaluate(basis1Y))
        const [basis2XValid, basis2XInvalid] = scope.branch(evaluator.evaluate(basis2X))
        const [basis2YValid, basis2YInvalid] = scope.branch(evaluator.evaluate(basis2Y))
        const [numXSegmentsValid, numXSegmentsInvalid] = scope.branch(evaluator.evaluate(numXSegments))
        const [numYSegmentsValid, numYSegmentsInvalid] = scope.branch(evaluator.evaluate(numYSegments))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(originXValid, originYValid, basis1XValid, basis1YValid, basis2XValid, basis2YValid, numXSegmentsValid, numYSegmentsValid, ctx),
            ([originX, originY, basis1X, basis1Y, basis2X, basis2Y, numXSegments, numYSegments, ctx]) =>
                createMeshGeometryAttributeData(
                    applyGeometryOperator(
                        (
                            originX: ValueRef | number,
                            originY: ValueRef | number,
                            basis1X: ValueRef | number,
                            basis1Y: ValueRef | number,
                            basis2X: ValueRef | number,
                            basis2Y: ValueRef | number,
                            numXSegments: ValueRef | number,
                            numYSegments: ValueRef | number,
                        ) => Primitives.grid2(ctx, originX, originY, basis1X, basis1Y, basis2X, basis2Y, numXSegments, numYSegments),
                        resolveScalarArg(originX),
                        resolveScalarArg(originY),
                        resolveScalarArg(basis1X),
                        resolveScalarArg(basis1Y),
                        resolveScalarArg(basis2X),
                        resolveScalarArg(basis2Y),
                        resolveScalarArg(numXSegments),
                        resolveScalarArg(numYSegments),
                    ),
                ),
            "meshGeometryGrid2",
        )

        return {
            evaluatedGeometryData: geometryAttributeData,
            invalidator: scope.phi(
                originXInvalid,
                originYInvalid,
                basis1XInvalid,
                basis1YInvalid,
                basis2XInvalid,
                basis2YInvalid,
                numXSegmentsInvalid,
                numYSegmentsInvalid,
            ),
        }
    }
}
